import React, { useState, useEffect } from 'react';
import { Save, X, Eye, Code, Type, Image as ImageIcon, Layout } from 'lucide-react';

interface CMSContent {
  id: string;
  title: string;
  type: 'page' | 'post' | 'media' | 'template';
  status: 'published' | 'draft' | 'archived';
  author: string;
  lastModified: string;
  content?: string;
  url?: string;
  excerpt?: string;
  tags?: string[];
}

interface ContentEditorProps {
  isOpen: boolean;
  onClose: () => void;
  content: CMSContent | null;
  onSave: (content: CMSContent) => void;
  isNew?: boolean;
}

const ContentEditor: React.FC<ContentEditorProps> = ({
  isOpen,
  onClose,
  content,
  onSave,
  isNew = false
}) => {
  const [formData, setFormData] = useState<CMSContent>({
    id: '',
    title: '',
    type: 'page',
    status: 'draft',
    author: 'Admin',
    lastModified: new Date().toISOString(),
    content: '',
    url: '',
    excerpt: '',
    tags: []
  });

  const [activeTab, setActiveTab] = useState<'content' | 'preview'>('content');
  const [saving, setSaving] = useState(false);
  const [editorMode, setEditorMode] = useState<'visual' | 'code'>('visual');
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Rich text editor functions
  const insertHtmlTag = (tag: string, hasClosing: boolean = true) => {
    const textarea = document.getElementById('content-textarea') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.content?.substring(start, end) || '';
    
    let replacement = '';
    if (hasClosing) {
      replacement = `<${tag}>${selectedText}</${tag}>`;
    } else {
      replacement = `<${tag}>`;
    }
    
    const newContent = 
      (formData.content?.substring(0, start) || '') + 
      replacement + 
      (formData.content?.substring(end) || '');
    
    handleInputChange('content', newContent);
    
    // Reset focus and cursor position
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + replacement.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  useEffect(() => {
    if (content) {
      setFormData(content);
    } else if (isNew) {
      setFormData({
        id: Date.now().toString(),
        title: '',
        type: 'page',
        status: 'draft',
        author: 'Admin',
        lastModified: new Date().toISOString(),
        content: '',
        url: '',
        excerpt: '',
        tags: []
      });
    }
  }, [content, isNew]);

  const handleInputChange = (field: keyof CMSContent, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      setSaveMessage({ type: 'error', text: 'Please enter a title' });
      return;
    }

    setSaving(true);
    setSaveMessage(null);
    
    try {
      const url = isNew 
        ? `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/cms`
        : `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/cms/${formData.id}`;
      
      const method = isNew ? 'POST' : 'PUT';
      
      const requestBody = {
        title: formData.title,
        type: formData.type.endsWith('s') ? formData.type.slice(0, -1) : formData.type,
        content: formData.content || '',
        status: formData.status,
        excerpt: formData.excerpt || '',
        url: formData.url || '',
        tags: formData.tags || [],
        author: formData.author || 'Admin'
      };

      console.log('Saving content:', { method, url, requestBody });
      
      const token = localStorage.getItem('skillseek_admin_token') || localStorage.getItem('token');
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      console.log('Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Save successful:', data);
        
        onSave(data.content);
        setSaveMessage({ type: 'success', text: data.message || 'Content saved successfully!' });
        
        // Close modal after brief success message
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        const errorData = await response.json().catch(() => null);
        console.error('Save failed:', response.status, errorData);
        
        let errorMessage = 'Failed to save content.';
        if (errorData) {
          errorMessage = errorData.message || errorData.error || errorMessage;
        } else if (response.status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (response.status === 403) {
          errorMessage = 'You do not have permission to save content.';
        } else if (response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        }
        
        setSaveMessage({ type: 'error', text: errorMessage });
      }
    } catch (error) {
      console.error('Error saving content:', error);
      setSaveMessage({ 
        type: 'error', 
        text: `Network error: ${error instanceof Error ? error.message : 'Please check your connection and try again.'}` 
      });
    } finally {
      setSaving(false);
    }
  };

  const getPreviewContent = () => {
    if (formData.type === 'page' || formData.type === 'post') {
      return (
        <div>
          <h1 style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '1rem', color: 'var(--text-color)' }}>
            {formData.title || 'Untitled'}
          </h1>
          {formData.excerpt && (
            <p style={{ fontSize: '1.25rem', marginBottom: '2rem', color: 'var(--text-dark-gray)', lineHeight: '1.6' }}>
              {formData.excerpt}
            </p>
          )}
          <div dangerouslySetInnerHTML={{ __html: formData.content || '<p style="color: var(--text-dark-gray);">No content yet...</p>' }} />
        </div>
      );
    }
    return <p style={{ color: 'var(--text-dark-gray)' }}>Preview not available for this content type.</p>;
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop fade show" onClick={onClose} />
      <div className="modal fade show d-block custom_cms_page" tabIndex={-1} style={{ zIndex: 1055 }}>
        <div className="modal-dialog modal-xl modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title d-flex align-items-center gap-2">
                {formData.type === 'page' && <Type size={20} />}
                {formData.type === 'post' && <Code size={20} />}
                {isNew ? `New ${formData.type.charAt(0).toUpperCase() + formData.type.slice(1)}` : `Edit ${formData.type.charAt(0).toUpperCase() + formData.type.slice(1)}`}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
              />
            </div>

            <div className="modal-body p-0">
              {/* Tabs */}
              <div className="border-bottom px-3 pt-3">
                <ul className="nav nav-tabs nav-fill" role="tablist">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'content' ? 'active' : ''}`}
                      onClick={() => setActiveTab('content')}
                    >
                      <Code size={16} className="me-2" />
                      Content
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'preview' ? 'active' : ''}`}
                      onClick={() => setActiveTab('preview')}
                    >
                      <Eye size={16} className="me-2" />
                      Preview
                    </button>
                  </li>
                </ul>
              </div>

              {/* Content Tab */}
              {activeTab === 'content' && (
                <div className="p-4">
                  <div className="row">
                    <div className="col-lg-8">
                      {/* Title */}
                      <div className="mb-3">
                        <label className="form-label fw-medium">Title *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          placeholder="Enter title..."
                        />
                      </div>

                      {/* Content Editor */}
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <label className="form-label fw-medium mb-0">Content</label>
                          <div className="btn-group btn-group-sm" role="group">
                            <button
                              type="button"
                              className={`btn ${editorMode === 'visual' ? 'btn-primary' : 'btn-outline-primary'}`}
                              onClick={() => setEditorMode('visual')}
                            >
                              <Eye size={14} className="me-1" />
                              Visual
                            </button>
                            <button
                              type="button"
                              className={`btn ${editorMode === 'code' ? 'btn-primary' : 'btn-outline-primary'}`}
                              onClick={() => setEditorMode('code')}
                            >
                              <Code size={14} className="me-1" />
                              HTML
                            </button>
                          </div>
                        </div>

                        {editorMode === 'visual' ? (
                          <div className="border rounded">
                            {/* Rich Text Toolbar */}
                            <div className="border-bottom p-2 bg-light">
                              <div className="btn-toolbar" role="toolbar">
                                <div className="btn-group me-2" role="group">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => insertHtmlTag('h1')}
                                    title="Heading 1"
                                  >
                                    H1
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => insertHtmlTag('h2')}
                                    title="Heading 2"
                                  >
                                    H2
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => insertHtmlTag('h3')}
                                    title="Heading 3"
                                  >
                                    H3
                                  </button>
                                </div>
                                <div className="btn-group me-2" role="group">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => insertHtmlTag('strong')}
                                    title="Bold"
                                  >
                                    <strong>B</strong>
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => insertHtmlTag('em')}
                                    title="Italic"
                                  >
                                    <em>I</em>
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => insertHtmlTag('u')}
                                    title="Underline"
                                  >
                                    <u>U</u>
                                  </button>
                                </div>
                                <div className="btn-group me-2" role="group">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => insertHtmlTag('p')}
                                    title="Paragraph"
                                  >
                                    P
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => insertHtmlTag('br', false)}
                                    title="Line Break"
                                  >
                                    BR
                                  </button>
                                </div>
                                <div className="btn-group me-2" role="group">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => insertHtmlTag('ul')}
                                    title="Unordered List"
                                  >
                                    UL
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => insertHtmlTag('ol')}
                                    title="Ordered List"
                                  >
                                    OL
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => insertHtmlTag('li')}
                                    title="List Item"
                                  >
                                    LI
                                  </button>
                                </div>
                                <div className="btn-group" role="group">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => insertHtmlTag('a href=""')}
                                    title="Link"
                                  >
                                    Link
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => insertHtmlTag('img src="" alt=""', false)}
                                    title="Image"
                                  >
                                    Img
                                  </button>
                                </div>
                              </div>
                            </div>
                            
                            {/* Visual Editor Textarea */}
                            <textarea
                              id="content-textarea"
                              className="form-control border-0"
                              rows={12}
                              value={formData.content}
                              onChange={(e) => handleInputChange('content', e.target.value)}
                              placeholder="Start typing your content here... Use the toolbar above to add HTML formatting."
                              style={{ 
                                resize: 'vertical',
                                minHeight: '300px'
                              }}
                            />
                          </div>
                        ) : (
                          <textarea
                            className="form-control"
                            rows={15}
                            value={formData.content}
                            onChange={(e) => handleInputChange('content', e.target.value)}
                            placeholder="Enter your HTML content here..."
                            style={{ 
                              fontFamily: 'Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                              fontSize: '14px'
                            }}
                          />
                        )}
                        
                        <div className="form-text">
                          {editorMode === 'visual' 
                            ? 'Use the toolbar buttons to add HTML formatting. Select text and click a button to wrap it with tags.'
                            : 'Edit the raw HTML code. Switch to Visual mode for easier formatting with the toolbar.'
                          }
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-4">
                      {/* Publishing Options */}
                      <div className="card">
                        <div className="card-header">
                          <h6 className="card-title mb-0">Publishing</h6>
                        </div>
                        <div className="card-body">
                          <div className="mb-3">
                            <label className="form-label fw-medium">Status</label>
                            <select
                              className="form-select"
                              value={formData.status}
                              onChange={(e) => handleInputChange('status', e.target.value)}
                            >
                              <option value="draft">Draft</option>
                              <option value="published">Published</option>
                              <option value="archived">Archived</option>
                            </select>
                          </div>

                          <div className="mb-3">
                            <label className="form-label fw-medium">Type</label>
                            <select
                              className="form-select"
                              value={formData.type}
                              onChange={(e) => handleInputChange('type', e.target.value)}
                              disabled={!isNew}
                            >
                              <option value="page">Page</option>
                              <option value="post">Post</option>
                            </select>
                          </div>

                          <div className="text-muted small">
                            Last modified: {new Date(formData.lastModified).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Preview Tab */}
              {activeTab === 'preview' && (
                <div className="p-4">
                  <div className="row justify-content-center">
                    <div className="col-lg-8">
                      <div className="preview-content">
                        {getPreviewContent()}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              {saveMessage && (
                <div className={`save-message ${saveMessage.type}`} style={{ marginRight: 'auto' }}>
                  {saveMessage.text}
                </div>
              )}
              
              <div className="d-flex align-items-center gap-2">
                {saving && (
                  <div className="saving-indicator">
                    <div className="saving-spinner"></div>
                    Saving content...
                  </div>
                )}
                
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success d-flex align-items-center gap-2"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Save Content
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
  };

export default ContentEditor; 